import { FC, memo, useEffect, useState } from "react";
import { ModalWindow } from "componentsNew/modals/ModalWindow";
import { useScheduleCellTheme } from "hooks/useTheme";
import { ScheduleSelectItem } from "types/Schedule";
import { UserData } from "types/User";
import {
  getScheduleReportForUserDate,
  isConsecutiveSameRoleLimitReached,
  isScheduleSelected,
  repeatingDispOrVisor,
  returnRoleHours,
  returnRoleName,
} from "utils/scheduleHelpers";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import { getTimeAfterPost } from "utils/filters";
import { SUPERADMIN } from "constants/constants";
import { Box, Tooltip, Typography } from "@mui/material";
import Error from "@material-ui/icons/Error";
import { useGetByHelperOf } from "hooks/useScheduleHooks";
import { daScheduleRoleKeyToId } from "mocks/roleDA";
import AddIcon from "@mui/icons-material/Add";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { EditScheduleModalContent } from "componentsNew/modals/modalContent/ScheduleModelContent";
import { addScheduleToCopyListAction, addScheduleToDleteListAction } from "actions/scheduleActions";

interface PropsType {
  date?: string;
  userData: UserData;
  classes: any;
  currentUserRole: number;
  idListForDelete: ScheduleSelectItem[];
}

export const ScheduleDetails: FC<PropsType> = memo(
  ({ date, userData, classes, currentUserRole, idListForDelete }) => {
    const [deleteStep, setDeleteStep] = useState(false);
    const report = useTypeSelectors((state) =>
      getScheduleReportForUserDate(state.scheduleTable.dataList, userData.id, date),
    );
    const [selectedRole, setSelectedRole] = useState(report?.role);

    useEffect(() => {
      setSelectedRole(report?.role);
    }, [report?.role]);
    const mode = useTypeSelectors((state) => state.scheduleTable.mode);
    const idListForCopy = useTypeSelectors((state) => state.scheduleTable.idListForCopy);
    const currentStation = useTypeSelectors((state) => state.scheduleTable.scheduleFilters.station);
    const isCellDisabledByDuplicate = useTypeSelectors((state) => {
      if (state.scheduleTable.mode !== "copy" || !date) return false;
      return repeatingDispOrVisor(
        state.scheduleTable.dataList,
        date,
        state.scheduleTable.reportCopy?.role,
        state.scheduleTable.scheduleFilters.station,
      );
    });
    const { helperOf } = useGetByHelperOf(date, report?.helper_of);

    const isConsecutiveRoleLimit = useTypeSelectors((state) =>
      isConsecutiveSameRoleLimitReached(state.scheduleTable.dataList, userData.id, date),
    );
    const { theme } = useScheduleCellTheme(report);
    const userId = userData.id;
    const modalName = `schedule-edit-report-${date}-${userId}-modal`;
    const isModalOpen = useTypeSelectors(
      (state) => state.modalState.isOpen && state.modalState.modalName === modalName,
    );
    const isCellDisabled = date
      ? getTimeAfterPost(date, currentUserRole === SUPERADMIN ? -1 : 0)
      : true;

    useEffect(() => {
      if (!isModalOpen) setDeleteStep(false);
    }, [isModalOpen]);

    return (
      <ModalWindow
        isDisabled={
          (mode === "delete" && !report) ||
          (mode === "delete" && report?.role === null) ||
          isCellDisabledByDuplicate ||
          Boolean(report?.station && report?.station !== Number(currentStation)) ||
          isCellDisabled
        }
        modal_name={modalName}
        buttonType={"custom"}
        modalTitle={`Edit Schedule`}
        customOnClick={
          mode === "copy"
            ? addScheduleToCopyListAction(date, userId)
            : mode === "delete"
              ? addScheduleToDleteListAction(date, userId)
              : null
        }
        copyScheduleReport={
          Boolean(report) &&
          !deleteStep &&
          selectedRole !== daScheduleRoleKeyToId.helper
        }
        scheduleReport={report}
        customButtonElement={
          <Box
            className={`${isScheduleSelected(idListForDelete, date, userId) || isScheduleSelected(idListForCopy, date, userId) ? classes.selectedBorder : ""} ${isConsecutiveRoleLimit ? classes.consecutiveBorder : ""} ${report || isCellDisabled ? classes.scheduleBox : classes.emptyScheduleBox} ${report || isCellDisabled ? theme.backgroundClass : ""}`}
          >
            {report ? (
              <>
                {report.is_available ? (
                  <>
                    {report?.role !== null ? (
                      <Box
                        className={`${![daScheduleRoleKeyToId.clientCancelled, daScheduleRoleKeyToId.mycuCancelled, daScheduleRoleKeyToId.rejected, daScheduleRoleKeyToId.absent].includes(report?.role) ? classes.cellContent : ""}`}
                      >
                        {!report?.is_published && (
                          <Tooltip title={<Typography>Not Published</Typography>}>
                            <Typography variant="h3">
                              <Error className={`${classes.unpublishedIcon} ${theme.colorClass}`} />
                            </Typography>
                          </Tooltip>
                        )}
                        {report?.station !== Number(currentStation) && (
                          <Typography
                            className={`${classes.scheduleHoursText} ${theme.colorClass}`}
                          >
                            {report?.station__title}
                          </Typography>
                        )}
                        <Typography className={`boldFont ${theme.colorClass}`}>
                          {returnRoleName(report?.role)}
                        </Typography>
                        {report?.role === daScheduleRoleKeyToId.helper &&
                        report?.station === Number(currentStation) ? (
                          <Typography
                            className={`${classes.scheduleHoursText} ${theme.colorClass}`}
                          >
                            {helperOf ? `${helperOf?.first_name} ${helperOf?.last_name}` : ""}
                          </Typography>
                        ) : (
                          ""
                        )}

                        {![
                          daScheduleRoleKeyToId.clientCancelled,
                          daScheduleRoleKeyToId.mycuCancelled,
                          daScheduleRoleKeyToId.rejected,
                          daScheduleRoleKeyToId.absent,
                        ].includes(report?.role) ? (
                          <Box className={classes.scheduleHours}>
                            <AccessTimeOutlinedIcon
                              className={`${classes.scheduleHoursIcon} ${theme.colorClass}`}
                              // fontSize="small"
                            />
                            <Typography
                              className={`${classes.scheduleHoursText} ${theme.colorClass}`}
                            >
                              {returnRoleHours(report?.role, report?.role_hours, report?.station)}
                              {` · Start at ${report.shift_start_time?.slice(0, 5)}`}
                            </Typography>
                          </Box>
                        ) : (
                          ""
                        )}
                      </Box>
                    ) : (
                      <Typography className={`boldFont ${theme.colorClass}`}>Available</Typography>
                    )}
                  </>
                ) : (
                  <Typography className={`boldFont ${theme.colorClass}`}>Unavailable</Typography>
                )}
              </>
            ) : (
              <>
                {!isCellDisabled ? (
                  <AddIcon className={classes.addScheduleIcon} fontSize="medium" />
                ) : (
                  <Typography className={`boldFont ${theme.colorClass}`}>Unavailable</Typography>
                )}
              </>
            )}
          </Box>
        }
      >
        <EditScheduleModalContent
          date={date}
          user={userData}
          schedule={report}
          deleteStep={deleteStep}
          setDeleteStep={setDeleteStep}
          onRoleChange={(role) => setSelectedRole(role)}
        />
      </ModalWindow>
    );
  },
);
